import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { PORTFOLIO_DISPLAY_NAMES } from '../../utils/portfolioNames';

const ICREditor = () => {
  const [icrList, setIcrList] = useState([]);         // ordered [{portfolio_code, name, suite, icr, ...}]
  const [icrData, setIcrData] = useState({});         // portfolio_code -> item
  const [editedValues, setEditedValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchICRData();
  }, []);

  const fetchICRData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/admin/portfolio-icr');
      const list = response.data || [];
      setIcrList(list);
      const dataMap = {};
      const values = {};
      list.forEach((item) => {
        dataMap[item.portfolio_code] = item;
        values[item.portfolio_code] = item.icr;
      });
      setIcrData(dataMap);
      setEditedValues(values);
    } catch (err) {
      console.error('Error fetching ICR data:', err);
      setMessage({ type: 'error', text: 'Failed to load ICR data' });
    } finally {
      setIsLoading(false);
    }
  };

  // Group rows by suite in the order the backend returns them (registry by display order,
  // then building blocks last).
  const groups = useMemo(() => {
    const g = [];
    const idx = {};
    icrList.forEach((item) => {
      const label = item.suite || 'Other';
      if (idx[label] === undefined) { idx[label] = g.length; g.push({ label, items: [] }); }
      g[idx[label]].items.push(item);
    });
    return g;
  }, [icrList]);

  const handleValueChange = (code, value) => {
    setEditedValues((prev) => ({ ...prev, [code]: value }));
  };

  const changedCodes = () => Object.keys(editedValues).filter(
    (code) => icrData[code] && Number(editedValues[code]) !== Number(icrData[code].icr)
  );
  const hasChanges = () => changedCodes().length > 0;

  const handleSaveAll = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const items = changedCodes().map((code) => ({
        portfolio_code: code,
        icr: parseFloat(editedValues[code]) || 0,
      }));
      if (!items.length) { setIsSaving(false); return; }
      await api.put('/api/admin/portfolio-icr/bulk', { items });
      setMessage({ type: 'success', text: `Saved ${items.length} ICR value${items.length === 1 ? '' : 's'}` });
      fetchICRData();
    } catch (err) {
      console.error('Error saving ICR data:', err);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save ICR values' });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getDisplayName = (item) => item.name || PORTFOLIO_DISPLAY_NAMES[item.portfolio_code] || item.portfolio_code;

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading ICR data...</p>
        </div>
      </div>
    );
  }

  const changeCount = changedCodes().length;

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">ICR Management</h5>
        <button
          className="btn btn-primary"
          onClick={handleSaveAll}
          disabled={isSaving || !hasChanges()}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            changeCount > 0 ? `Save ${changeCount} change${changeCount === 1 ? '' : 's'}` : 'Save All'
          )}
        </button>
      </div>

      <div className="card-body">
        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label="Close"></button>
          </div>
        )}

        <div className="alert alert-info py-2" style={{ fontSize: '0.85rem' }}>
          <strong>Note:</strong> dashboard and blender cost figures for registry SAAs now come from
          the <strong>Fee Calculator</strong> (RG 97 management fees and costs of each SAA's canonical
          registry portfolio). ICR values here only apply to asset-class building blocks (Cash,
          Australian Equities, ...) and any SAA whose canonical portfolio has no fee data yet.
        </div>

        <p className="text-muted mb-4">
          Set the Investment Cost Ratio (ICR) for every portfolio in the registry (and asset-class
          building blocks). Values are a percentage (e.g. 0.45 = 0.45%). A portfolio with no stored
          ICR shows 0.00 — enter a value and Save to create it.
        </p>

        {groups.map((group) => (
          <div key={group.label} className="mb-4">
            <h6 className="text-uppercase text-muted border-bottom pb-2 mb-3">{group.label}</h6>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Portfolio</th>
                    <th style={{ width: '20%' }}>ICR (%)</th>
                    <th style={{ width: '20%' }}>Last Updated</th>
                    <th style={{ width: '20%' }}>Updated By</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => {
                    const code = item.portfolio_code;
                    const isChanged = Number(editedValues[code]) !== Number(item.icr);
                    const isNew = item.id == null;
                    if (item.superseded) {
                      return (
                        <tr key={code} style={{ opacity: 0.5, backgroundColor: '#f8f9fa' }}>
                          <td>
                            <strong>{getDisplayName(item)}</strong>
                            <br />
                            <small className="text-muted">{code}</small>
                          </td>
                          <td>
                            <span className="fw-semibold">{item.fee_cost != null ? `${Number(item.fee_cost).toFixed(2)}%` : '—'}</span>
                            <br />
                            <span className="badge text-bg-light" title="Cost is derived from the canonical portfolio's PDS fee entry — the ICR value here is no longer used">
                              from Fee Calculator
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {isNew ? <span className="badge text-bg-light">not set</span> : formatDate(item.updated_at)}
                            </small>
                          </td>
                          <td>
                            <small className="text-muted">{item.updated_by || '-'}</small>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={code}>
                        <td>
                          <strong>{getDisplayName(item)}</strong>
                          <br />
                          <small className="text-muted">{code}</small>
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`form-control form-control-sm ${isChanged ? 'border-warning' : ''}`}
                            style={{ maxWidth: '120px' }}
                            value={editedValues[code] ?? ''}
                            onChange={(e) => handleValueChange(code, parseFloat(e.target.value) || 0)}
                            step="0.01"
                            min="0"
                          />
                        </td>
                        <td>
                          <small className="text-muted">
                            {isNew ? <span className="badge text-bg-light">not set</span> : formatDate(item.updated_at)}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">{item.updated_by || '-'}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ICREditor;
